// ContextSection — the rich source-context header a review panel shows above
// the decision controls. Fetches the joined live subject rows (the call, the
// break, the mandate state, …) lazily when a panel opens, and renders the
// normalised blocks generically. Absent/empty values are dropped server-side
// and again here, so there is never filler: a kind with no extra useful
// context renders nothing at all.

import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Quote } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import type { ContextBlock, ContextField, ContextTranscriptLine } from '@septima/contract';
import { Pill } from '@/components/ui';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

// -------- data --------

export function useReviewContext(
    subjectKind: string,
    id: string | null | undefined,
): ContextBlock[] {
    const { data } = useQuery<ContextBlock[]>({
        queryKey: ['review-context', subjectKind, id],
        queryFn: async () => {
            const res = await api.reviewQueue.context({ query: { subjectKind, id: id ?? '' } });
            if (res.status !== 200) throw new Error(`context fetch ${res.status}`);
            return res.body.blocks;
        },
        enabled: Boolean(id),
        staleTime: 30_000,
    });
    return data ?? [];
}

/** The transcript block, if the context carries one (call drafts only). */
export function transcriptFrom(blocks: ContextBlock[]): ContextTranscriptLine[] | null {
    const block = blocks.find((b) => b.type === 'transcriptExcerpt');
    return block && block.type === 'transcriptExcerpt' ? block.lines : null;
}

// -------- rendering --------

const TONE_VARIANT = {
    positive: 'green',
    warning: 'amber',
    negative: 'red',
} as const;

function hasValue(v: string | null | undefined): v is string {
    return typeof v === 'string' && v.trim().length > 0;
}

function FieldRow({ field }: { field: ContextField }) {
    if (!hasValue(field.value)) return null;
    const variant = field.tone && field.tone !== 'default' ? TONE_VARIANT[field.tone] : null;
    return (
        <div className="flex items-baseline gap-2">
            <span className="w-32 flex-shrink-0 text-annotation text-[rgb(var(--color-text-tertiary))]">
                {field.label}
            </span>
            {variant ? (
                <Pill variant={variant} size="sm" label={field.value} />
            ) : (
                <span className="flex-1 text-body-medium text-[rgb(var(--color-text-primary))]">
                    {field.value}
                </span>
            )}
        </div>
    );
}

function BlockCard({
    title,
    subject,
    children,
}: {
    title?: string;
    subject?: boolean;
    children: React.ReactNode;
}) {
    return (
        <section
            className={cn(
                'rounded-card border px-3 py-2.5 flex flex-col gap-1.5',
                subject
                    ? 'border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface-secondary)/0.4)]'
                    : 'border-[rgb(var(--color-divider)/0.6)]',
            )}
        >
            {title && (
                <h3 className="text-annotation font-medium uppercase tracking-wide text-[rgb(var(--color-text-tertiary))]">
                    {title}
                </h3>
            )}
            {children}
        </section>
    );
}

function ContextBlockView({ block }: { block: ContextBlock }) {
    switch (block.type) {
        case 'fields': {
            const visible = block.fields.filter((f) => hasValue(f.value));
            if (visible.length === 0) return null;
            return (
                <BlockCard title={block.title} subject={block.subject}>
                    <div className="flex flex-col gap-1">
                        {visible.map((f, i) => (
                            <FieldRow key={`${f.label}-${i}`} field={f} />
                        ))}
                    </div>
                </BlockCard>
            );
        }
        case 'prose': {
            if (!hasValue(block.text)) return null;
            return (
                <BlockCard title={block.title}>
                    <p className="text-body-medium text-[rgb(var(--color-text-secondary))] whitespace-pre-wrap leading-snug">
                        {block.text}
                    </p>
                </BlockCard>
            );
        }
        case 'sideBySide': {
            if (!hasValue(block.custodian) && !hasValue(block.internal) && !hasValue(block.diff)) {
                return null;
            }
            return (
                <BlockCard title={block.title ?? 'Break evidence'} subject>
                    <div className="grid grid-cols-2 gap-2">
                        {hasValue(block.custodian) && (
                            <div className="flex flex-col gap-0.5">
                                <span className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                                    Custodian
                                </span>
                                <span className="text-body-medium font-mono tabular-nums text-[rgb(var(--color-text-primary))]">
                                    {block.custodian}
                                </span>
                            </div>
                        )}
                        {hasValue(block.internal) && (
                            <div className="flex flex-col gap-0.5">
                                <span className="text-annotation text-[rgb(var(--color-text-tertiary))]">
                                    Internal
                                </span>
                                <span className="text-body-medium font-mono tabular-nums text-[rgb(var(--color-text-primary))]">
                                    {block.internal}
                                </span>
                            </div>
                        )}
                    </div>
                    {hasValue(block.diff) && (
                        <div className="text-annotation text-[rgb(var(--color-rag-amber))]">
                            {block.diff}
                        </div>
                    )}
                </BlockCard>
            );
        }
        case 'linkedEntity': {
            if (!hasValue(block.value)) return null;
            return (
                <BlockCard>
                    <FieldRow field={{ label: block.label, value: block.value }} />
                </BlockCard>
            );
        }
        case 'transcriptExcerpt':
            // Rendered separately (collapsible) so it never crowds the header.
            return <TranscriptBlock lines={block.lines} title={block.title} />;
    }
}

function TranscriptBlock({ lines, title }: { lines: ContextTranscriptLine[]; title?: string }) {
    const [open, setOpen] = useState(false);
    if (lines.length === 0) return null;
    return (
        <section className="rounded-card border border-[rgb(var(--color-divider)/0.6)]">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-1.5 px-3 py-2 text-annotation text-[rgb(var(--color-text-secondary))] hover:text-[rgb(var(--color-text-primary))]"
            >
                {open ? (
                    <ChevronDown className="w-3.5 h-3.5" />
                ) : (
                    <ChevronRight className="w-3.5 h-3.5" />
                )}
                {title ?? 'Transcript'} ({lines.length} lines)
            </button>
            {open && (
                <div className="px-3 pb-2.5 flex flex-col gap-1 max-h-80 overflow-y-auto">
                    {lines.map((l, i) => (
                        <TranscriptLineRow key={l.lineId ?? i} line={l} />
                    ))}
                </div>
            )}
        </section>
    );
}

function TranscriptLineRow({ line }: { line: ContextTranscriptLine }) {
    return (
        <div className="flex items-start gap-2 text-annotation">
            {hasValue(line.speaker) && (
                <span className="w-24 flex-shrink-0 font-medium text-[rgb(var(--color-text-secondary))]">
                    {line.speaker}
                </span>
            )}
            <span className="flex-1 text-[rgb(var(--color-text-primary))]">{line.text}</span>
        </div>
    );
}

/** Render an arbitrary set of context blocks (used by the single-decision
 *  panels: findings, narratives, composer drafts). */
export function ContextBlocks({ blocks }: { blocks: ContextBlock[] }) {
    const rendered = blocks.filter((b) => b.type !== 'transcriptExcerpt' || b.lines.length > 0);
    if (rendered.length === 0) return null;
    return (
        <div className="flex flex-col gap-2">
            {blocks.map((block, i) => (
                <ContextBlockView key={i} block={block} />
            ))}
        </div>
    );
}

/** Lazy context header for a single review subject. Renders nothing until the
 *  fetch resolves and only if there is real context to show. */
export function ContextSection({
    subjectKind,
    id,
}: {
    subjectKind: string;
    id: string | null | undefined;
}) {
    const blocks = useReviewContext(subjectKind, id);
    if (blocks.length === 0) return null;
    return <ContextBlocks blocks={blocks} />;
}

// -------- per-citation transcript expander (call drafts) --------

/** Expand the ±2 transcript lines around a cited line, on demand. Used by the
 *  call-drafts items so a reviewer can verify an extraction in context without
 *  leaving the panel. Renders nothing when the cited line can't be located. */
export function CitedTranscriptExpander({
    lines,
    lineId,
}: {
    lines: ContextTranscriptLine[] | null;
    lineId: string | null | undefined;
}) {
    const [open, setOpen] = useState(false);
    const window = useMemo(() => {
        if (!lines || !hasValue(lineId)) return null;
        const idx = lines.findIndex((l) => l.lineId === lineId);
        if (idx < 0) return null;
        return lines.slice(Math.max(0, idx - 2), idx + 3).map((l) => ({
            line: l,
            cited: l.lineId === lineId,
        }));
    }, [lines, lineId]);
    if (!window) return null;
    return (
        <div className="flex flex-col gap-1">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="self-start inline-flex items-center gap-1 text-annotation text-[rgb(var(--color-text-tertiary))] hover:text-[rgb(var(--color-text-secondary))]"
            >
                <Quote className="w-3 h-3" />
                {open ? 'Hide transcript context' : 'Show in transcript'}
            </button>
            {open && (
                <div className="rounded-card border border-[rgb(var(--color-divider)/0.6)] px-2.5 py-2 flex flex-col gap-1">
                    {window.map(({ line, cited }, i) => (
                        <div
                            key={line.lineId ?? i}
                            className={cn(
                                'flex items-start gap-2 text-annotation',
                                cited && 'text-[rgb(var(--color-text-primary))] font-medium',
                                !cited && 'text-[rgb(var(--color-text-tertiary))]',
                            )}
                        >
                            {hasValue(line.speaker) && (
                                <span className="w-20 flex-shrink-0">{line.speaker}</span>
                            )}
                            <span className="flex-1">{line.text}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
